import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { GetStrategyVariance, RebalanceLots, postManualRebalance } from '../../Redux/Actions/transactionService';
import { Box, Typography, Button, IconButton, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Select, MenuItem, CircularProgress, Avatar, TextField, Modal, Paper, FormControl, InputLabel } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import DeleteIcon from '@mui/icons-material/Delete';
import { useParams, useNavigate } from 'react-router-dom';
import customSuccessAlert from '../../Alerts/customSuccessAlert';
import customFailAlert from '../../Alerts/customFailAlert';


import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import AutoRebalanceConfirmationModal from '../../Component/Rebalancing/AutoRebalanceConfirmationModal';
import AutoRebalanceWiseModal from '../../Component/Rebalancing/AutoRebalanceWiseModal';
import AssetVarianceModal from '../../Component/Rebalancing/AssetVarianceModal';
import '../../Styles/AssetVarianceTable.css';

function getInitials(name) {
    if (!name) return '';
    const words = name.trim().split(' ');
    if (words.length === 1) {
        return words[0].substring(0, 2).toUpperCase();
    }
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
}

const AutoRebalancePage = ({ setShowAuto, investors = [], selectedInvestors = [] }) => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // State for the page
    const [orders, setOrders] = useState([
        { orderType: '', asset: '', allocation: '', limitPrice: '', limitPriceFocused: false }
    ]); // For asset allocation orders
    const [investorList, setInvestorList] = useState([]);
    const [assetOptions, setAssetOptions] = useState([]);
    const [varianceData, setVarianceData] = useState([]);
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [canProceed, setCanProceed] = useState(false);
    const [isRebalancing, setIsRebalancing] = useState(false);
    const [showAssetVarianceModal, setShowAssetVarianceModal] = useState(false);
    const [selectedInvestorForVariance, setSelectedInvestorForVariance] = useState(null);

    const [currentOrderType, setCurrentOrderType] = useState('');


    const [lotsPreview, setLotsPreview] = useState(null);
    const [showLotsModal, setShowLotsModal] = useState(false);
    const [rebalancePayload, setRebalancePayload] = useState(null);
    const [filteredVarianceData, setFilteredVarianceData] = useState([]); // Filtered variance data for table
    const [showUserWiseModal, setShowUserWiseModal] = useState(false);

    // Remove static asset options - will be fetched from API

    useEffect(() => {
        const fetchData = async () => {
            if (id) {
                setIsLoadingData(true);
                try {
                    const result = await dispatch(GetStrategyVariance({ strategyId: id }));
                    const data = result?.payload?.data || [];

                    // Filter data to only include selected investors if selectedInvestors is provided
                    let filteredData = data;

                    if (selectedInvestors && selectedInvestors.length > 0) {
                        const selectedInvestorIds = selectedInvestors.map(inv => inv.id);
                        filteredData = data.filter(user =>
                            selectedInvestorIds.includes(user?.userRole?.id)
                        );
                    }

                    // Build investor list - use the same structure as ManualRebalancePage
                    const investorObjs = [];
                    filteredData.forEach(user => {
                        const firstName = user?.userRole?.user?.userInfo?.firstName;
                        const userRoleId = user?.userRole?.id;
                        if (userRoleId) {
                            investorObjs.push({
                                ...user, // Include the full user data like in ManualRebalancePage
                                id: userRoleId,
                                name: firstName || '-',
                                firstName: firstName || '-'
                            });
                        }
                    });

                    setInvestorList(investorObjs);
                    setVarianceData(filteredData);

                    // Get asset options from strategy breakups (same as ManualRebalancePage)
                    const strategyBreakups = data[0]?.strategy?.breakups || [];
                    const assetOptions = strategyBreakups
                        .filter(b => b.proportion > 0 && b.asset && b.asset.symbol)
                        .map(b => ({
                            value: b.asset.symbol,
                            label: b.asset.symbol
                        }));
                    setAssetOptions(assetOptions);

                    // Initialize orders from strategy breakups
                    if (data[0]?.strategy?.breakups && data[0].strategy.breakups.length > 0) {
                        const breakups = data[0].strategy.breakups;
                        const initialOrders = breakups
                            .filter(b => b.proportion > 0)
                            .map(b => ({
                                asset: b.asset?.symbol || '',
                                allocation: b.proportion || 0,
                                orderType: '', // No default order type
                                limitPrice: '',
                                limitPriceFocused: false
                            }));
                        setOrders(initialOrders);
                        setCurrentOrderType(''); // No default order type
                    }
                } catch (err) {
                    console.error('Error fetching strategy variance:', err);
                } finally {
                    setIsLoadingData(false);
                }
            }
        };

        fetchData();
    }, [dispatch, id, selectedInvestors]);

    // Handle order field changes
    const handleOrderFieldChange = (idx, field, value) => {
        setOrders(prev =>
            prev.map((item, i) =>
                i === idx ? { ...item, [field]: value } : item
            )
        );
    };

    // Input validation functions
    const validateNumericInput = (value) => {
        return /^$|^[0-9]*\.?[0-9]*$/.test(value);
    };

    const validateAllocationInput = (value) => {
        return /^$|^[0-9]*\.?[0-9]*$/.test(value);
    };

    const handleAllocationChange = (idx, value) => {
        if (validateAllocationInput(value)) {
            handleOrderFieldChange(idx, 'allocation', value);
        }
    };

    const handleLimitPriceChange = (idx, value) => {
        if (validateNumericInput(value)) {
            handleOrderFieldChange(idx, 'limitPrice', value);
        }
    };

    // Sync current order type when orders change
    useEffect(() => {
        if (orders.length > 0 && orders[0]?.orderType) {
            setCurrentOrderType(orders[0].orderType);
        } else if (orders.length === 0) {
            setCurrentOrderType('');
        }
    }, [orders]);





    // Check if can proceed
    useEffect(() => {
        const usedAssets = orders.map(order => order.asset).filter(asset => asset && asset.trim() !== '');
        const availableAssetsForAdd = assetOptions.filter(option => !usedAssets.includes(option.value));
        const hasAvailableAssets = availableAssetsForAdd.length > 0;

        const hasDuplicateAssets = () => {
            const assetCounts = {};
            orders.forEach(order => {
                if (order.asset && order.asset.trim() !== '') {
                    assetCounts[order.asset] = (assetCounts[order.asset] || 0) + 1;
                }
            });
            return Object.values(assetCounts).some(count => count > 1);
        };

        const hasValidCurrentOrders = orders.length > 0 && orders.every(order =>
            order.asset && order.asset.trim() !== '' &&
            order.allocation !== '' && order.allocation !== null && order.allocation !== undefined &&
            !isNaN(Number(order.allocation))
        );

        const isMarket = currentOrderType === 'MKT';
        const isLimit = currentOrderType === 'LMT';
        const allLimitPricesFilled = isLimit
            ? orders.every(order => order.limitPrice && order.limitPrice.trim() !== '' && !isNaN(Number(order.limitPrice)) && Number(order.limitPrice) > 0)
            : true;

        const hasValidOrders = orders.length > 0 && orders.every(order =>
            order.asset && order.asset.trim() !== '' &&
            order.allocation !== '' && order.allocation !== null && order.allocation !== undefined &&
            !isNaN(Number(order.allocation))
        );

        const canProceed = hasValidOrders && currentOrderType && (isMarket || (isLimit && allLimitPricesFilled));

        setCanProceed(canProceed && investorList.length > 0);
    }, [orders, investorList, currentOrderType, assetOptions]);

    // Remove investor from list
    const handleRemoveInvestor = (index) => {
        if (investorList.length > 1) {
            const updatedInvestors = investorList.filter((_, i) => i !== index);
            setInvestorList(updatedInvestors);
        }
    };

    // Handle showing asset variance for an investor
    const handleShowAssetVariance = (investor) => {
        setSelectedInvestorForVariance(investor);
        setShowAssetVarianceModal(true);
    };

    // Function to preview lots using RebalanceLots API
    const handlePreviewLots = async () => {
        const userRoleIds = investorList.map(inv => inv.userRole?.id).filter(Boolean);
        if (!id || userRoleIds.length === 0) return;
        const body = {
            strategyId: id,
            userRoleIds
        };
        try {
            const response = await dispatch(RebalanceLots({ body }));
            const lotsData = response?.payload?.data;
            setLotsPreview(lotsData);
        } catch (error) {
            console.error('Error previewing lots:', error);
        }
    };

    // Helper to transform lotsPreview to rebalanceData format for the modal
    const getRebalanceTableData = () => {
        if (!lotsPreview) return [];
        let rows = [];
        Object.entries(lotsPreview).forEach(([userId, lots], idx) => {
            const userObj = Array.isArray(varianceData) ? varianceData.find(u => u.userRole?.id === userId)
                : null;
            const investorName = userObj?.userRole?.user?.userInfo?.firstName || "-";
            // Create a unique identifier for this investor to ensure proper grouping
            const uniqueInvestorId = `${investorName}_${userId}`;

            lots.forEach(lot => {
                rows.push({
                    investor: investorName,
                    uniqueInvestorId: uniqueInvestorId, // Add unique identifier for proper grouping
                    asset: lot.asset?.name || '-',
                    symbol: lot.asset?.symbol || '-',
                    shareCount: lot.shareCount !== undefined ? Number(lot.shareCount).toFixed(2) : '-',
                    sellQuantity: lot.sellQuantity !== undefined ? Number(lot.sellQuantity).toFixed(2) : '-',
                    daysHeld: lot.daysHeld !== undefined ? lot.daysHeld : '-',
                    costPrice: lot.costPrice !== undefined ? Number(lot.costPrice).toFixed(2) : '-',
                    marketPrice: lot.marketPrice !== undefined ? Number(lot.marketPrice).toFixed(5) : '-',
                    totalProfit: lot.totalProfit !== undefined ? Number(lot.totalProfit).toFixed(2) : '0'
                });
            });
        });
        return rows;
    };

    const handleLotsModalClose = () => {
        setShowLotsModal(false);
    };

    const handleLotsModalConfirm = () => {
        setShowLotsModal(false);
        // Update filtered data based on current investorList before opening the modal
        const remainingInvestorIds = investorList.map(inv => inv.userRole?.id);
        const filtered = varianceData.filter(user =>
            remainingInvestorIds.includes(user?.userRole?.id)
        );
        setFilteredVarianceData(filtered);
        setShowUserWiseModal(true);
    };

    const handleUserWiseModalClose = () => {
        setShowUserWiseModal(false);
        setShowLotsModal(true); // Go back to the previous modal (RebalanceConfirmationModal)
    };

    const handleUserWiseModalConfirm = async () => {
        setIsRebalancing(true);
        try {
            const strategyId = varianceData[0]?.strategy?.id || '';
            const type = orders[0]?.orderType === 'LMT' ? 'LMT' : 'MKT';

            const positiveOrders = orders.filter(o => Number(o.allocation) > 0);
            const orderItems = type === 'LMT' ? positiveOrders.map(order => ({
                symbol: order.asset,
                price: Number(order.limitPrice),
                isWashTrade: false
            })) : [];

            const userRoleAssets = investorList.map(investor => {
                const userVarianceData = varianceData.find(user => user.userRole?.id === investor.userRole?.id);

                return {
                    userRoleId: investor.userRole?.id,
                    assets: positiveOrders.map(order => {
                        const assetVariance = userVarianceData?.assetsVariance?.find(asset =>
                            asset.asset?.symbol === order.asset
                        );

                        return {
                            symbol: order.asset,
                            isWash: assetVariance?.isWashTrade === false,
                            proportion: Number(order.allocation)
                        };
                    })
                };
            });

            const payload = [{
                type,
                ...(type === 'LMT' && { orderItems: orderItems }),
                strategyId,
                userRoleAssets
            }];

            const response = await dispatch(postManualRebalance({ body: payload }));
            const statusCode = response?.payload?.statusCode;

            if (statusCode === 201) {
                customSuccessAlert('Auto rebalance completed successfully!').then((result) => {
                    if (result.isConfirmed) {
                        navigate(-1);
                    }
                });
            } else {
                customFailAlert(response?.payload?.message || 'Auto rebalance failed.');
            }
        } catch (err) {
            customFailAlert('Auto rebalance failed. Please try again.');
        } finally {
            setIsRebalancing(false);
            setShowUserWiseModal(false);
        }
    };

    // Handle confirm rebalance
    const handleConfirmRebalance = () => {
        setShowLotsModal(true);
    };

    // Handle next button click
    const handleNextClick = async () => {
        if (canProceed) {
            await handlePreviewLots(); // <-- This will fetch and set lotsPreview
            handleConfirmRebalance();  // <-- This will open the modal
        }
    };

    // Get strategy name from varianceData
    const strategyName = varianceData[0]?.strategy?.name || '';

    // Check for available assets
    const usedAssets = orders.map(order => order.asset).filter(asset => asset && asset.trim() !== '');
    const availableAssetsForAdd = assetOptions.filter(option => !usedAssets.includes(option.value));
    const hasAvailableAssets = availableAssetsForAdd.length > 0;

    // Check for valid current orders
    const hasValidCurrentOrders = orders.length > 0 && orders.every(order =>
        order.asset && order.asset.trim() !== '' &&
        order.allocation !== '' && order.allocation !== null && order.allocation !== undefined &&
        !isNaN(Number(order.allocation))
    );



    // Don't render the main content until data is loaded
    if (isLoadingData) {
        return (
            <Box sx={{ p: 4, background: '#fafbfc', minHeight: '100vh' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <IconButton onClick={() => setShowAuto(false)} sx={{ mr: 1 }}>
                            <ArrowBackIosNewIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                            Auto Rebalance
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                    <CircularProgress />
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 4, background: '#fafbfc', minHeight: '100vh' }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton onClick={() => setShowAuto(false)} sx={{ mr: 1 }}>
                        <ArrowBackIosNewIcon fontSize="small" />
                    </IconButton>
                    <Typography variant="h5" sx={{
                        fontWeight: 'bold',
                        color: 'var(--text-text-neutral-1, #141218)',
                        // color: 'var(--M3-Tokens-ref-neutral-neutral70, #636363)',
                        fontSize: '32px',
                        fontStyle: 'normal',
                        fontWeight: 500,
                        lineHeight: '40px'
                    }}>
                        Auto Rebalance <span style={{

                            color: 'var(--M3-Tokens-ref-neutral-neutral70, #636363)',
                            fontSize: '32px',
                            fontStyle: 'normal',
                            fontWeight: 500,
                            lineHeight: '40px'
                        }}> | {strategyName}</span>
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    disabled={!canProceed}
                    onClick={handleNextClick}
                    sx={{
                        background: canProceed ? '#1976d2' : '#e0e0e0',
                        color: canProceed ? '#fff' : '#bdbdbd',
                        boxShadow: 'none',
                        fontWeight: 500,
                        fontSize: 16,
                        borderRadius: 2,
                        px: 4,
                        py: 1,
                        minWidth: 90,
                        '&:hover': {
                            background: canProceed ? '#1565c0' : '#e0e0e0',
                        },
                    }}
                >
                    Next
                </Button>
            </Box>

            {/* Introductory Text */}
            <Typography sx={{ mb: 3, color: '#666', fontSize: '18px' }}>
                You will be able to see variances and buying/selling assets in the next steps.
            </Typography>

            {/* Main Content - Two Panels */}
            <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
                {/* Left Panel - Investor List */}
                <Paper sx={{ flex: 1, p: 2, borderRadius: 2, boxShadow: 0, border: '1px solid #e0e0e0', width: '100%', height: 'fit-content' }}>
                    <Typography variant="subtitle2" sx={{ color: '#666', mb: 1 }}>
                        Investor List
                    </Typography>
                    <Divider sx={{ my: 1 }} />

                    <Box sx={{
                        maxHeight: investorList.length > 3 ? 400 : 'auto',
                        overflowY: investorList.length > 3 ? 'auto' : 'visible',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        padding: '8px 24px 8px 0' // top, right, bottom, left

                    }}>
                        {investorList.map((investor, index) => {
                            const investorName = investor.name || "-";

                            return (
                                <Box
                                    key={`investor-${index}`}
                                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                        <Box
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: '50%',
                                                background: '#e0e3e7',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: 600,
                                                color: '#757575',
                                                mr: 2,
                                                fontSize: 16,
                                                textAlign: 'center',
                                                overflow: 'hidden',
                                                flexShrink: 0,
                                            }}
                                        >
                                            {getInitials(investorName)}
                                        </Box>
                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                            <Typography variant="body1" sx={{
                                                fontWeight: 500, fontSize: '16px', color: '#212121'
                                            }}>
                                                {investorName}
                                            </Typography>
                                            <Button
                                                variant="text"
                                                size="small"
                                                sx={{
                                                    color: '#1976d2',
                                                    textTransform: 'none',
                                                    fontWeight: 500,
                                                    fontSize: 16,
                                                    minWidth: 0,
                                                    p: 0,
                                                    textDecoration: 'underline',
                                                    alignSelf: 'flex-start',
                                                    mt: 0.5,
                                                    '&:hover': {
                                                        backgroundColor: 'transparent',
                                                        textDecoration: 'underline'
                                                    }
                                                }}
                                                onClick={() => handleShowAssetVariance(investor)}
                                            >
                                                See Asset Variance
                                            </Button>
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <DeleteIcon sx={{
                                            color: investorList.length <= 1 ? '#ccc' : '#1976d2',
                                            fontSize: 20,
                                            opacity: investorList.length <= 1 ? 0.5 : 1
                                        }} />
                                        <Button
                                            variant="text"
                                            size="small"
                                            sx={{
                                                color: investorList.length <= 1 ? '#ccc' : '#1976d2',
                                                textTransform: 'none',
                                                fontWeight: 500,
                                                fontSize: 16,
                                                minWidth: 0,
                                                p: 0,
                                                textDecoration: 'underline',
                                                '&:hover': {
                                                    backgroundColor: 'transparent',
                                                    textDecoration: 'underline'
                                                },
                                                '&:disabled': {
                                                    color: '#ccc',
                                                    cursor: 'not-allowed',
                                                    textDecoration: 'none',
                                                    '&:hover': {
                                                        backgroundColor: 'transparent',
                                                        textDecoration: 'none'
                                                    }
                                                }
                                            }}
                                            onClick={() => handleRemoveInvestor(index)}
                                            disabled={investorList.length <= 1}
                                        >
                                            Remove
                                        </Button>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </Paper>

                {/* Right Panel - Order List */}
                <Paper sx={{ flex: 1, p: 2, borderRadius: 2, boxShadow: 0, border: '1px solid #e0e0e0', width: '100%', minHeight: 'fit-content' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center'}}>
                        <Typography variant="subtitle2" sx={{ color: '#666'}}>Order List</Typography>
                    </Box>
                    <Divider sx={{ my: 1 }} />

                    {isLoadingData ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <>
                            {/* Order Type Selection */}
                            <Box sx={{ my: 2, width: '100%' }}>
                                <FormControl fullWidth size="large">
                                    <InputLabel id="order-type-label">Order Type</InputLabel>
                                    <Select
                                        labelId="order-type-label"
                                        value={currentOrderType}
                                        label="Order Type"
                                        onChange={e => {
                                            const newOrderType = e.target.value;
                                            setCurrentOrderType(newOrderType);

                                            if (orders.length > 0) {
                                                setOrders(prev => prev.map(order => ({
                                                    ...order,
                                                    orderType: newOrderType,
                                                    limitPrice: newOrderType === 'MKT' ? '' : order.limitPrice,
                                                    limitPriceFocused: newOrderType === 'LMT'
                                                })));
                                            }
                                        }}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': {
                                                    border: 'none !important'
                                                },
                                                '&:hover fieldset': {
                                                    border: 'none !important'
                                                },
                                                '&.Mui-focused fieldset': {
                                                    border: 'none !important'
                                                }
                                            },
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                border: 'none !important'
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                border: 'none !important'
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                border: 'none !important'
                                            },
                                            '& .MuiMenuItem-root.Mui-selected': {
                                                backgroundColor: '#E3F2FD !important'
                                            },
                                            '& .MuiMenuItem-root.Mui-selected:hover': {
                                                backgroundColor: '#E3F2FD !important'
                                            },
                                            '& .MuiPaper-root': {
                                                backgroundColor: 'var(--M3-Tokens-white, #FFF) !important',
                                                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 1px 3px 1px rgba(0, 0, 0, 0.15) !important'
                                            },
                                            '& .MuiMenuItem-root': {
                                                color: 'var(--M3-sys-light-on-surface, var(--M3-Tokens-sys-light-on-surface, #211C1C))',
                                                fontFamily: 'Roboto',
                                                fontSize: '16px',
                                                fontStyle: 'normal',
                                                fontWeight: 400,
                                                lineHeight: '24px',
                                                letterSpacing: '0.5px'
                                            }
                                        }}
                                    >
                                        <MenuItem value={"MKT"}>Market</MenuItem>
                                        <MenuItem value={"LMT"}>Limit</MenuItem>

                                    </Select>
                                </FormControl>
                            </Box>
                            {currentOrderType && currentOrderType !== '' && (
                                <>
                                    {orders.length > 0 && orders.map((order, idx) => (
                                        <Box key={`order-${idx}-${order.asset || 'empty'}`} sx={{ display: 'flex', alignItems: 'center', mb: 2, width: '100%', gap: 2, border: '1px solid var(--M3-Tokens-ref-tertiary-tertiary10, #D2D7E9)', p: 1, background: '#FFF' }}>
                                            <FormControl sx={{ minWidth: 160 }}>
                                                <InputLabel>Asset</InputLabel>
                                                <Select
                                                    value={order.asset}
                                                    label="Asset"
                                                    disabled={currentOrderType === 'MKT' || currentOrderType === 'LMT'}
                                                    onChange={e => handleOrderFieldChange(idx, 'asset', e.target.value)}
                                                    sx={{
                                                        '& .MuiPaper-root': {
                                                            backgroundColor: 'var(--M3-Tokens-white, #FFF)',
                                                            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 1px 3px 1px rgba(0, 0, 0, 0.15)'
                                                        }
                                                    }}
                                                >
                                                    {assetOptions
                                                        .filter(option => {
                                                            const isCurrentOrder = order.asset === option.value;
                                                            const isUsedInOtherOrders = orders.some((otherOrder, otherIdx) =>
                                                                otherIdx !== idx && otherOrder.asset === option.value && otherOrder.asset.trim() !== ''
                                                            );
                                                            return isCurrentOrder || !isUsedInOtherOrders;
                                                        })
                                                        .map((option) => (
                                                            <MenuItem key={option.value} value={option.value}>
                                                                {option.label}
                                                            </MenuItem>
                                                        ))}
                                                </Select>
                                            </FormControl>
                                            <TextField
                                                variant="outlined"
                                                color="primary"
                                                size="medium"
                                                label="Allocation"
                                                value={order.allocation}
                                                disabled={currentOrderType === 'MKT' || currentOrderType === 'LMT'}
                                                onChange={e => handleAllocationChange(idx, e.target.value)}
                                                sx={{ minWidth: 80, background: '#fff' }}
                                                inputProps={{ min: 0, max: 100 }}
                                                InputProps={{
                                                    endAdornment: <Typography sx={{ fontWeight: 600, color: '#D2D7E9', ml: 1 }}>%</Typography>
                                                }}
                                            />
                                            {currentOrderType === 'LMT' && (
                                                <TextField
                                                    variant="outlined"
                                                    color="primary"
                                                    size="medium"
                                                    label="Limit Price"
                                                    value={order.limitPrice}
                                                    onChange={e => handleLimitPriceChange(idx, e.target.value)}
                                                    sx={{ minWidth: 140, background: '#fff' }}
                                                    inputProps={{ min: 0, step: 'any' }}
                                                    autoFocus={order.limitPriceFocused}
                                                    onFocus={() => handleOrderFieldChange(idx, 'limitPriceFocused', true)}
                                                    onBlur={() => handleOrderFieldChange(idx, 'limitPriceFocused', false)}
                                                    InputProps={{
                                                        startAdornment: order.limitPrice ? <Typography sx={{ fontWeight: 600, color: '#000000', mr: 1 }}>$</Typography> : null
                                                    }}
                                                />
                                            )}

                                        </Box>
                                    ))}
                                </>
                            )}
                        </>
                    )}


                </Paper>
            </Box>

            {/* Asset Variance Modal */}
            <AssetVarianceModal
                open={showAssetVarianceModal}
                onClose={() => setShowAssetVarianceModal(false)}
                selectedInvestorForVariance={selectedInvestorForVariance}
                varianceData={varianceData}
            />

            {/* Confirm User-Wise Rebalance Modal */}
            <AutoRebalanceConfirmationModal
                open={showLotsModal}
                onClose={handleLotsModalClose}
                isFullscreen={false}
                rebalanceData={getRebalanceTableData()}
                onConfirm={handleLotsModalConfirm}
                setIsFullscreen={() => { }}
                strategyName={strategyName}
                orders={orders}
            />

            {/* Final user-wise modal */}
            <AutoRebalanceWiseModal
                open={showUserWiseModal}
                onClose={handleUserWiseModalClose}
                onConfirm={handleUserWiseModalConfirm}
                strategyName={strategyName}
                filteredVarianceData={filteredVarianceData}
                varianceData={varianceData}
                orders={orders}
                isRebalancing={isRebalancing}
            />
        </Box>
    );
};

export default AutoRebalancePage;
